import {
  AutoPrintEnum,
  CompanyProps,
  CompanyStatusEnum,
  DeliveryModeEnum,
} from 'modules/companies/types';
import useEditCompany from 'modules/companies/hooks/use-edit-company';
import companiesKeys from 'modules/companies/util/companies-keys';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import Edition from 'shared/layout/edition';
import useFormInputs from 'shared/migration-components/form/util';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import useSegmentsByCitySelect from 'modules/segments/hooks/use-segments-by-city-select';
import usePaymentMethodsByCitySelect from 'modules/payment-methods/hooks/use-payment-methods-by-city-select';
import {SponsorTypeEnum} from 'modules/coupons/types';

const EditCompany: React.FC = () => {
  const {id} = useParams();
  const {
    getInput,
    getSelect,
    getAddressInput,
    getMultiSelect,
    getNumberInput,
    getImagePicker,
    getSwitch,
    getPriceInput,
  } = useFormInputs<CompanyProps>();
  const accessControls = useAccessControls();
  const [cityId, setCityId] = useState<string>();
  const paymentMethodsSelect = usePaymentMethodsByCitySelect(cityId);
  const segmentsSelect = useSegmentsByCitySelect(cityId);

  const handleSubmit = (data: CompanyProps) => {
    const formData = new FormData();

    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('cnpj', data.cnpj);
    formData.append('phone', data.phone);
    if (data.pixKey) formData.append('pixKey', data.pixKey);
    formData.append('cityId', data.cityId);
    formData.append('deliveryTime', String(data.deliveryTime));
    formData.append('deliveryPrice', String(data.deliveryPrice));
    if (data.deliveryMode) formData.append('deliveryMode', data.deliveryMode);
    formData.append('deliveryByCompany', String(data.deliveryByCompany));
    formData.append(
      'useCompanyDeliveryZones',
      String(data.useCompanyDeliveryZones),
    );
    formData.append('freeDelivery', String(data.freeDelivery));
    formData.append('freeDeliverySponsor', String(data.freeDeliverySponsor));
    formData.append('active', String(data.active));
    formData.append('available', String(data.available));
    if (data.freeDeliveryThreshold)
      formData.append(
        'freeDeliveryThreshold',
        String(data.freeDeliveryThreshold),
      );
    formData.append('allowPickup', String(data.allowPickup));
    formData.append('allowPickupPayment', String(data.allowPickupPayment));
    formData.append('segmentsIds', JSON.stringify(data.segmentsIds));
    formData.append('status', data.status);
    formData.append(
      'excludedPaymentMethodsIds',
      JSON.stringify(data.excludedPaymentMethodsIds),
    );
    formData.append('address', JSON.stringify(data.address));

    if ((data.logo as any) instanceof File) formData.append('logo', data.logo);
    if ((data.banner as any) instanceof File)
      formData.append('banner', data.banner);
    if (data.autoPrint) formData.append('autoPrint', data.autoPrint);
    if (!isNaN(Number(data.minOrderValue)))
      formData.append('minOrderValue', String(data.minOrderValue));

    return formData;
  };

  return (
    <Edition<CompanyProps>
      border
      onSubmit={() => useEditCompany(id!)}
      {...{
        handleSubmit,
        onFormStateChange: (data) => {
          if (data.cityId && data.cityId !== cityId) setCityId(data.cityId);
        },
      }}
      detailsUrl={`/companies/${id}`}
      translationKey="CompanyProps"
      queryKey={companiesKeys.details(id!)}
      inputs={[
        getInput({
          name: 'name',
          placeholder: 'Nome da empresa',
        }),
        getInput({name: 'email'}),
        getInput({name: 'cnpj', gridColumn: 6}),
        getInput({name: 'phone', gridColumn: 6}),
        getInput({name: 'pixKey'}),
        getMultiSelect({
          name: 'segmentsIds',
          data: segmentsSelect.data,
        }),
        getAddressInput({label: 'Endereço', name: 'address'}),
        getSwitch({
          name: 'deliveryByCompany',
          hidden: () => !!accessControls.companyId,
        }),
        getSwitch({
          name: 'useCompanyDeliveryZones',
          hidden: (data) =>
            !!accessControls.companyId || data.deliveryByCompany,
        }),
        getSelect({
          name: 'deliveryMode',
          data: [
            {label: 'Grátis', value: DeliveryModeEnum.free},
            {label: 'Preço fixo', value: DeliveryModeEnum.fixed},
            {label: 'Bairro/Distância', value: DeliveryModeEnum.neighborhood},
          ],
          hidden: (data) =>
            !!accessControls.companyId || !data.deliveryByCompany,
        }),
        getPriceInput({
          name: 'deliveryPrice',
          hidden: (data) =>
            accessControls.companyId
              ? true
              : data.deliveryMode !== DeliveryModeEnum.fixed ||
                !data.deliveryByCompany,
        }),
        getSwitch({
          name: 'freeDelivery',
          hidden: () => !!accessControls.companyId,
        }),
        getPriceInput({
          name: 'freeDeliveryThreshold',
          hidden: (data) => !!data.freeDelivery,
        }),
        getSelect({
          name: 'freeDeliverySponsor',
          data: [
            {label: 'Empresa', value: SponsorTypeEnum.COMPANY},
            {label: 'Cidade', value: SponsorTypeEnum.CITY},
          ],
          hidden: () => !!accessControls?.companyId,
        }),
        getMultiSelect({
          name: 'excludedPaymentMethodsIds',
          ...paymentMethodsSelect,
          hidden: () => !!accessControls.companyId,
        }),
        getNumberInput({
          name: 'deliveryTime',
        }),
        getSelect({
          name: 'status',
          data: [
            {label: 'Ativa', value: CompanyStatusEnum.active},
            {label: 'Inativa', value: CompanyStatusEnum.inactive},
            {
              label: 'Aguardando aprovação',
              value: CompanyStatusEnum.awaiting_approval,
            },
          ],
          hidden: () =>
            !accessControls.cityId && !accessControls.isFranchiseAccessControl,
        }),
        getSelect({
          name: 'autoPrint',
          data: [
            {
              label: 'Imprimir ao aceitar',
              value: AutoPrintEnum.order_accepted,
            },
            {
              label: 'Colocar em preparo e imprimir',
              value: AutoPrintEnum.skip_preparing,
            },
          ],
          hidden: () => !!accessControls.companyId,
        }),
        getPriceInput({name: 'minOrderValue'}),
        getSwitch({name: 'available'}),
        getSwitch({name: 'active'}),
        getSwitch({
          name: 'allowPickup',
          hidden: () => !!accessControls.companyId,
        }),
        getSwitch({
          name: 'allowPickupPayment',
          hidden: () => !!accessControls.companyId,
        }),
        getImagePicker({label: 'Logo', name: 'logo'}),
        getImagePicker({label: 'Banner', name: 'banner'}),
      ]}
    />
  );
};

export default EditCompany;
